import React, { useEffect, useState, useRef, useContext, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Grid,
  IconButton,
  Paper,
  Typography,
  TextField,
  Alert,
  CircularProgress,
  Avatar,
  Rating,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  Snackbar,
  Chip,
  Slider,
  Select,
  MenuItem,
  Tab,
  Tabs,
  Menu,
  ListItemIcon,
  ListItemText,
  Stack
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Comment as CommentIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  LibraryAdd as LibraryAddIcon,
  LibraryBooks as LibraryBooksIcon,
  PlaylistPlay as PlaylistPlayIcon,
  Instagram as InstagramIcon,
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  Language as LanguageIcon
} from '@mui/icons-material';
import axios from 'axios';
import AuthContext from '../../context/AuthContext';
import { getCoverArtUrl, getProfileImageUrl, getAudioFileUrl } from '../../utils/urlHelpers';
import { getSocialDisplayLink } from '../../utils/socialHelpers';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const formatTime = (seconds) => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};

// Helper function to get artist display name
const getArtistDisplayName = (artist) => artist?.artistName || artist?.username;

// Helper function to get formatted artist names list
const getArtistsList = (release) => {
  if (!release) return '';
  
  return release.tracks.reduce((artists, track) => {
    // Add main artists
    const mainArtists = track.featuredArtists?.filter(a => a.role === 'main') || [];
    mainArtists.forEach(artist => {
      if (artist.name && !artists.includes(artist.name)) {
        artists.push(artist.name);
      }
    });
    // Add featured artists
    track.featuredArtists?.filter(a => a.role !== 'main').forEach(artist => {
      if (!artists.includes(artist.name)) {
        artists.push(artist.name);
      }
    });
    return artists;
  }, []).join(', ');
};

const PromoPage = () => {
  const { promoCode, trackId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  
  // Track/Release state
  const [release, setRelease] = useState(null);
  const [track, setTrack] = useState(null);
  const [selectedTrackId, setSelectedTrackId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [playing, setPlaying] = useState(false);
  
  // Curator library state
  const [libraryEntry, setLibraryEntry] = useState(null);
  const [showLibraryDialog, setShowLibraryDialog] = useState(false);
  const [libraryNotes, setLibraryNotes] = useState('');
  const [libraryRating, setLibraryRating] = useState(0);
  const [libraryTags, setLibraryTags] = useState('');
  
  // Comment form state
  const [commentDialogOpen, setCommentDialogOpen] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [userRating, setUserRating] = useState(0);
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [commentError, setCommentError] = useState('');
  const [verifyEmail, setVerifyEmail] = useState('');
  const [activeTab, setActiveTab] = useState('verify');  // Add this state
  
  // Track comments
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [showComments, setShowComments] = useState(false);
  
  // Comment editing state
  const [editingComment, setEditingComment] = useState(null);
  const [editCommentText, setEditCommentText] = useState('');
  const [editCommentRating, setEditCommentRating] = useState(0);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  
  // Comment menu state
  const [commentMenuAnchor, setCommentMenuAnchor] = useState(null);
  const [selectedComment, setSelectedComment] = useState(null);
  
  // Download state
  const [canDownload, setCanDownload] = useState(false);
  const [downloadEmail, setDownloadEmail] = useState('');
  const [downloadDialogOpen, setDownloadDialogOpen] = useState(false);
  const [downloadingRelease, setDownloadingRelease] = useState(false);
  const [downloadingTrack, setDownloadingTrack] = useState(null);
  const [selectedFormat, setSelectedFormat] = useState('mp3');
  const [availableFormats, setAvailableFormats] = useState(['mp3']);
  
  // Success message
  const [successMessage, setSuccessMessage] = useState('');
  const [downloadMessage, setDownloadMessage] = useState('');
  
  // Refs
  const audioRef = useRef(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    const fetchTrackData = async () => {
      
      try {
        setLoading(true);
        setError(null);
        
        // Check if we have a direct trackId (from promo/track/:trackId route)
        if (trackId) {
          const res = await axios.get(`${API_URL}/tracks/${trackId}`);
          setTrack(res.data);
          setSelectedTrackId(res.data._id);
        }
        // Otherwise check for promoCode (from promo/release/:promoCode route)
        else if (promoCode) {
          const res = await axios.get(`${API_URL}/releases/promo/${promoCode}`);
          setRelease(res.data);
          // Set first track as default
          if (res.data.tracks.length > 0) {
            setTrack(res.data.tracks[0]);
            setSelectedTrackId(res.data.tracks[0]._id);
          }
        }
        
        setLoading(false);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load content. Please try again later.');
        setLoading(false);
      }
      
    };
    
    fetchTrackData();
  }, [promoCode, trackId]);

  // Remove the automatic comment fetching useEffect and create a function to fetch comments
  const fetchComments = useCallback(async (email = null) => {
    
    if (!track && !release) {
      return;
    }
    
    try {
      setLoadingComments(true);
      
      let endpoint;
      const headers = {};
      
      // Add auth headers for logged-in users
      if (user) {
        const token = localStorage.getItem('token');
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }
      }
      
      if (release && release.tracks && release.tracks.length > 1) {
        // For releases with multiple tracks, get release comments
        endpoint = `${API_URL}/feedback/release/${release._id}`;
        if (!user && email) {
          endpoint += `?guestEmail=${email}&isPromo=true`;
        } else {
          endpoint += '?isPromo=true';
        }
      } else {
        // For single tracks, get track comments
        endpoint = `${API_URL}/feedback/track/${track._id}`;
        if (!user && email) {
          endpoint += `?guestEmail=${email}&isPromo=true`;
        } else {
          endpoint += '?isPromo=true';
        }
      }
      
      
      const res = await axios.get(endpoint, { headers });
      
      setComments(res.data);
      setShowComments(true);
      setLoadingComments(false);
      
    } catch (err) {
      setLoadingComments(false);
    }
    
  }, [track, release, user]);

  // Auto-load comments for logged-in users
  useEffect(() => {
    if (user && (track || release)) {
      fetchComments();
      // Note: curators must also comment before downloading - don't automatically set canDownload
    }
  }, [user, track, release, fetchComments]);

  // Fetch available formats when track changes
  useEffect(() => {
    const fetchAvailableFormats = async () => {
      if (!track && !release) return;
      
      try {
        let response;
        if (release && release.tracks && release.tracks.length > 1) {
          // For multi-track releases, get release formats
          response = await axios.get(`${API_URL}/releases/${release._id}/formats`);
        } else if (track) {
          // For single tracks, get track formats
          response = await axios.get(`${API_URL}/tracks/${track._id}/formats`);
        } else {
          return;
        }
        
        setAvailableFormats(response.data.availableFormats || ['mp3']);
        
        // Set initial format to original format if available, otherwise MP3
        const originalFormat = response.data.originalFormat;
        if (originalFormat && response.data.availableFormats.includes(originalFormat)) {
          setSelectedFormat(originalFormat);
        } else {
          setSelectedFormat('mp3');
        }
      } catch (error) {
        // Fallback to MP3 only if formats can't be fetched
        setAvailableFormats(['mp3']);
        setSelectedFormat('mp3');
      }
    };
    
    fetchAvailableFormats();
  }, [track, release]);

  // Debug logging for state changes
  useEffect(() => {
  }, [user, track, release, canDownload, downloadEmail, comments.length, showComments]);

  useEffect(() => {
    if (!track) return;

    const audio = new Audio(getAudioFileUrl(track.audioFile));
    audioRef.current = audio;

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
    };

    const handleEnded = () => {
      setPlaying(false);
      setCurrentTime(0);
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('ended', handleEnded);
      audio.pause();
      audioRef.current = null;
    };
  }, [track]);
  
  const togglePlayPause = () => {
    if (!audioRef.current || !track) return;
    
    if (playing) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
      
      // Increment the play count when starting playback
      // Only increment when starting playback, not when pausing
      axios.post(`${API_URL}/tracks/${track._id}/play`)
        .then(response => {
        })
        .catch(error => {
        });
    }
    setPlaying(!playing);
  };

  const handleSliderChange = (_, newValue) => {
    if (!audioRef.current) return;
    const time = (newValue / 100) * duration;
    audioRef.current.currentTime = time;
    setCurrentTime(time);
  };

  // Handle downloading the track
  const downloadTrack = async (trackId = null, format = null) => {
    const targetTrackId = trackId || track?._id;
    const targetTrack = trackId ? 
      (release ? release.tracks.find(t => t._id === trackId) : track) : 
      track;
    const downloadFormat = format || selectedFormat || 'mp3';
    
    
    try {
      setDownloadingTrack(targetTrackId);
      setDownloadMessage('Preparing track download...');
      
      let url = `${API_URL}/tracks/download/${targetTrackId}`;
      const params = new URLSearchParams();
      
      // Add format parameter
      params.append('format', downloadFormat);
      
      // If user is not logged in, add the guest email as a query parameter
      if (!user && downloadEmail) {
        params.append('guestEmail', downloadEmail);
      }
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      // For all users, use fetch to handle downloads properly
      
      const headers = {};
      if (user) {
        const token = localStorage.getItem('token');
        headers['Authorization'] = `Bearer ${token}`;
      }

      setDownloadMessage(`Downloading track (${downloadFormat.toUpperCase()})...`);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: headers,
      });
      
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }
      
      // For successful responses, handle the download
      const blob = await response.blob();
      
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', `${targetTrack?.title || 'track'}.${downloadFormat}`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      
      setDownloadMessage('Download started! Check your browser downloads.');
      setTimeout(() => setDownloadMessage(''), 3000);
      
    } catch (error) {
      setError('Failed to download the track. Please try again later.');
      setDownloadMessage('');
    } finally {
      setDownloadingTrack(null);
    }
    
  };

  // Handle downloading the release
  const downloadRelease = async () => {
    
    try {
      setDownloadingRelease(true);
      setDownloadMessage('Preparing release download...');
      
      let url = `${API_URL}/releases/download/${release._id}`;
      const params = new URLSearchParams();
      
      // Add format parameter
      params.append('format', selectedFormat || 'mp3');
      
      // If user is not logged in, add the guest email as a query parameter
      if (!user && downloadEmail) {
        params.append('guestEmail', downloadEmail);
      }
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      // For all users, use fetch to handle downloads properly
      
      const headers = {};
      if (user) {
        const token = localStorage.getItem('token');
        headers['Authorization'] = `Bearer ${token}`;
      }

      setDownloadMessage(`Creating ZIP file (${(selectedFormat || 'mp3').toUpperCase()})...`);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: headers,
      });
      
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }
      
      // For successful responses, handle the download
      const blob = await response.blob();
      
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', `${release.title}.zip`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      
      setDownloadMessage('Download started! Check your browser downloads.');
      setTimeout(() => setDownloadMessage(''), 3000);
      
    } catch (error) {
      setError('Failed to download the release. Please try again later.');
      setDownloadMessage('');
    } finally {
      setDownloadingRelease(false);
    }
    
  };

  // Handle download verification dialog submit
  const handleDownloadVerify = async () => {
    
    if (!downloadEmail.trim()) {
      return;
    }

    try {
      
      const response = await axios.post(
        `${API_URL}/feedback/download-check/${track._id}`, 
        { guestEmail: downloadEmail.trim() }
      );
      
      
      if (response.data.canDownload) {
        // If we have a comment, add it to the list if not already there
        if (response.data.comment) {
          setDownloadDialogOpen(false);
          setCanDownload(true);
          setDownloadEmail(downloadEmail.trim());
          setSuccessMessage('Verified! You can now download the track.');
          
          // Add the verified comment to the comments list if it's not already there
          setComments(prevComments => {
            const exists = prevComments.some(c => 
              c.isGuest && c.guestEmail === `${downloadEmail.trim().substring(0, 2)}...${downloadEmail.trim().split('@')[1]}`
            );
            if (!exists && response.data.comment) {
              return [{
                ...response.data.comment,
                isGuest: true,
                guestEmail: `${downloadEmail.trim().substring(0, 2)}...${downloadEmail.trim().split('@')[1]}`,
                createdAt: response.data.comment.createdAt
              }, ...prevComments];
            }
            return prevComments;
          });
          
          // Proceed with download
          downloadTrack();
        } else {
          setDownloadDialogOpen(false);
          setCanDownload(true);
          setDownloadEmail(downloadEmail.trim());
          downloadTrack();
        }
      } else {
        // If no existing comment found, open comment dialog
        setCommentDialogOpen(true);
        setDownloadDialogOpen(false);
        setGuestEmail(downloadEmail.trim());
        setActiveTab('comment');
      }
    } catch (error) {
      setCommentError(error.response?.data?.message || 'Failed to verify comment');
      setCommentDialogOpen(true);
      setDownloadDialogOpen(false);
      setGuestEmail(downloadEmail.trim());
      setActiveTab('comment');
    }
    
  };

  // Handle verify comment handler
  const handleVerifyComment = async () => {
    
    if (!verifyEmail.trim()) {
      setCommentError('Please enter your email');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(verifyEmail.trim())) {
      setCommentError('Please enter a valid email address');
      return;
    }

    setSubmittingComment(true);
    setCommentError('');
    
    try {
      const email = verifyEmail.trim();
      
      const response = await axios.post(
        `${API_URL}/feedback/download-check/${track._id}`, 
        { guestEmail: email }
      );
      
      
      if (response.data.canDownload) {
        // Found existing comment - enable download and close dialog
        setCommentDialogOpen(false);
        setCanDownload(true);
        setDownloadEmail(email);
        setSuccessMessage('Verified! You can now download the track.');
        
        // Fetch comments for this email
        await fetchComments(email);
      } else {
        setShowComments(false);
        setComments([]);
        // No existing comment found
        setCommentError('No comment found with this email. Please leave a new comment.');
        setActiveTab('comment');
        setGuestEmail(email);
      }
    } catch (error) {
      setCommentError(error.response?.data?.message || 'Failed to verify comment');
      setShowComments(false);
      setComments([]);
    } finally {
      setSubmittingComment(false);
    }
    
  };

  // Handle comment submission
  const handleCommentSubmit = async () => {
    
    // Validate form
    if (!userRating) {
      setCommentError('Please provide a rating');
      return;
    }
    
    if (!commentText.trim()) {
      setCommentError('Please enter a comment');
      return;
    }
    
    if (!user) {
      // Guest comment validation
      if (!guestName.trim()) {
        setCommentError('Please enter your name');
        return;
      }
      
      if (!guestEmail.trim()) {
        setCommentError('Please enter your email');
        return;
      }
      
      // Simple email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(guestEmail.trim())) {
        setCommentError('Please enter a valid email address');
        return;
      }
    }
    
    setSubmittingComment(true);
    setCommentError('');
    
    try {
      const email = user ? null : guestEmail.trim();
      const commentData = {
        text: commentText.trim(),
        rating: userRating
      };
      
      // For guest users, add guest info
      if (!user) {
        commentData.guestName = guestName.trim();
        commentData.guestEmail = email;
      }
      
      // For release pages, comment on the release with favorite track
      if (release && release.tracks && release.tracks.length > 1) {
        commentData.releaseId = release._id;
        commentData.favoriteTrackId = selectedTrackId || release.tracks[0]._id;
      } else {
        // For single tracks or track-specific comments
        commentData.trackId = track._id;
      }
      
      // Prepare headers
      const headers = {};
      if (user) {
        const token = localStorage.getItem('token');
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }
      }
      
      // Submit the comment
      
      await axios.post(`${API_URL}/feedback`, commentData, { headers });
      
      
      // Reset form
      setCommentText('');
      setUserRating(0);
      setCommentDialogOpen(false);
      
      // Set download state
      setCanDownload(true);
      if (!user) {
        setDownloadEmail(email);
      }
      
      
      // Fetch and show comments - pass email only for guest users
      await fetchComments(user ? null : email);
      
      // Show success message
      setSuccessMessage('Thank you for your feedback! You can now download the track.');
      
      
    } catch (err) {
      
      setCommentError(err.response?.data?.message || 'Failed to submit your comment. Please try again.');
      setShowComments(false);
      setComments([]);
      
    } finally {
      setSubmittingComment(false);
    }
  };
  
  // Handle edit comment
  const handleEditComment = (comment) => {
    setEditingComment(comment);
    setEditCommentText(comment.text);
    setEditCommentRating(comment.rating);
    setEditDialogOpen(true);
  };

  // Handle save edited comment
  const handleSaveEditedComment = async () => {
    
    if (!editCommentText.trim()) {
      setCommentError('Please enter a comment');
      return;
    }
    
    if (!editCommentRating) {
      setCommentError('Please provide a rating');
      return;
    }
    
    try {
      setSubmittingComment(true);
      setCommentError('');
      
      const updateData = {
        text: editCommentText.trim(),
        rating: editCommentRating
      };
      
      // Add guest email if it's a guest comment
      if (editingComment.isGuest) {
        updateData.guestEmail = downloadEmail || guestEmail;
      }
      
      // Prepare headers
      const headers = {};
      if (user) {
        const token = localStorage.getItem('token');
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }
      }
      
      
      await axios.put(`${API_URL}/feedback/comment/${editingComment._id}`, updateData, { headers });
      
      
      // Update the comment in the local state
      setComments(prevComments => 
        prevComments.map(comment => 
          comment._id === editingComment._id 
            ? { ...comment, text: editCommentText.trim(), rating: editCommentRating }
            : comment
        )
      );
      
      // Close dialog and reset state
      setEditDialogOpen(false);
      setEditingComment(null);
      setEditCommentText('');
      setEditCommentRating(0);
      
      setSuccessMessage('Comment updated successfully!');
      
      
    } catch (err) {
      
      setCommentError(err.response?.data?.message || 'Failed to update comment. Please try again.');
    } finally {
      setSubmittingComment(false);
    }
  };

  // Handle delete comment
  const handleDeleteComment = async (comment) => {
    
    if (!window.confirm('Are you sure you want to delete this comment?')) {
      return;
    }
    
    try {
      const deleteData = {};
      
      // Add guest email if it's a guest comment
      if (comment.isGuest) {
        deleteData.guestEmail = downloadEmail || guestEmail;
      }
      
      // Prepare headers
      const headers = {};
      if (user) {
        const token = localStorage.getItem('token');
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }
      }
      
      
      await axios.delete(`${API_URL}/feedback/comment/${comment._id}`, { 
        headers,
        data: deleteData 
      });
      
      
      // Remove the comment from the local state
      setComments(prevComments => 
        prevComments.filter(c => c._id !== comment._id)
      );
      
      setSuccessMessage('Comment deleted successfully!');
      
      
    } catch (err) {
      
      setError(err.response?.data?.message || 'Failed to delete comment. Please try again.');
    }
  };

  // Check if user can edit/delete comment
  const canEditComment = (comment) => {
    if (comment.isGuest) {
      // For guest comments, check if the user has verified with an email that matches this comment
      // Note: comment.guestEmail is masked in the response, but we compare against the actual emails
      // stored in downloadEmail (from verification) or guestEmail (from new comment submission)
      const userEmail = downloadEmail || guestEmail;
      
      // If we don't have a verified email, guest can't edit
      if (!userEmail) {
        return false;
      }
      
      // Create the same masked format to compare
      const maskedUserEmail = `${userEmail.substring(0, 2)}...${userEmail.split('@')[1]}`;
      return comment.guestEmail === maskedUserEmail;
    } else {
      // Logged in user can edit their own comments
      return user && comment.user && comment.user._id === user._id;
    }
  };

  // Handle comment menu
  const handleCommentMenuOpen = (event, comment) => {
    setCommentMenuAnchor(event.currentTarget);
    setSelectedComment(comment);
  };

  const handleCommentMenuClose = () => {
    setCommentMenuAnchor(null);
    setSelectedComment(null);
  };

  const handleEditMenuClick = () => {
    handleEditComment(selectedComment);
    handleCommentMenuClose();
  };

  const handleDeleteMenuClick = () => {
    handleDeleteComment(selectedComment);
    handleCommentMenuClose();
  };

  // Curator-specific functions
  const fetchLibraryEntry = useCallback(async () => {
    if (!user || user.accountType !== 'curator' || (!track && !release)) return;
    
    try {
      const response = await axios.get(`${API_URL}/curator/library`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const entries = response.data.libraryEntries;
      const currentEntry = entries.find(entry => {
        if (release && release._id) {
          return entry.release._id === release._id;
        }
        if (track && track._id) {
          return entry.track && entry.track._id === track._id;
        }
        return false;
      });
      
      if (currentEntry) {
        setLibraryEntry(currentEntry);
        setLibraryNotes(currentEntry.notes || '');
        setLibraryRating(currentEntry.rating || 0);
        setLibraryTags(currentEntry.tags?.join(', ') || '');
      }
    } catch (error) {
    }
  }, [user, track, release]);

  const updateLibraryEntry = async () => {
    if (!user || user.accountType !== 'curator' || (!track && !release)) return;
    
    try {
      const targetRelease = release || track;
      const data = {
        releaseId: targetRelease._id,
        trackId: track && !release ? track._id : null,
        accessMethod: 'promo_link',
        notes: libraryNotes.trim(),
        rating: libraryRating,
        tags: libraryTags.split(',').map(tag => tag.trim()).filter(tag => tag)
      };
      
      if (libraryEntry) {
        // Update existing entry
        await axios.put(`${API_URL}/curator/library/${libraryEntry._id}`, {
          notes: data.notes,
          rating: data.rating,
          tags: data.tags
        }, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        });
      } else {
        // Create new entry
        await axios.post(`${API_URL}/curator/library`, data, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        });
      }
      
      setSuccessMessage('Library entry updated successfully!');
      setShowLibraryDialog(false);
      await fetchLibraryEntry();
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to update library entry');
    }
  };

  const navigateToPlayLog = () => {
    navigate('/curator/play-logs');
  };

  const navigateToLibrary = () => {
    navigate('/curator/library');
  };

  // Load library entry for curators
  useEffect(() => {
    if (user && user.accountType === 'curator' && (track || release)) {
      fetchLibraryEntry();
    }
  }, [user, track, release, fetchLibraryEntry]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }
  
  // Comment dialog component
  const renderCommentDialog = () => (
    <Dialog open={commentDialogOpen} onClose={() => setCommentDialogOpen(false)} fullWidth>
      <DialogTitle>Download Track</DialogTitle>
      <DialogContent>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
            <Tab label="Already commented?" value="verify" />
            <Tab label="New Comment" value="comment" />
          </Tabs>
        </Box>

        {activeTab === 'verify' && (
          <>
            <DialogContentText sx={{ mb: 2 }}>
              If you've already commented on this track, enter your email to verify and download.
            </DialogContentText>
            <TextField
              autoFocus
              margin="dense"
              label="Email Address"
              type="email"
              fullWidth
              variant="outlined"
              value={verifyEmail}
              onChange={(e) => setVerifyEmail(e.target.value)}
              error={!!commentError}
              helperText={commentError}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: '#666666' },
                  '&:hover fieldset': { borderColor: '#666666' },
                  '&.Mui-focused fieldset': { borderColor: '#a187f2' }
                }
              }}
            />
          </>
        )}

        {activeTab === 'comment' && (
          <>
            <DialogContentText sx={{ mb: 2 }}>
              {release ? 'Share your thoughts about this release and pick your favorite track!' : 'Share your thoughts about this track!'}
            </DialogContentText>
            
            {commentError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {commentError}
              </Alert>
            )}
            
            <Box sx={{ mb: 3 }}>
              <Typography component="legend" sx={{ mb: 1 }}>Rating:</Typography>
              <Rating
                name="rating"
                value={userRating}
                onChange={(event, newValue) => setUserRating(newValue)}
                precision={0.5}
                size="large"
                sx={{
                  '& .MuiRating-iconFilled': {
                    color: '#a187f2',
                  },
                  '& .MuiRating-iconHover': {
                    color: '#a187f2',
                  }
                }}
              />
            </Box>
            
            {release && (
              <Box sx={{ mb: 3 }}>
                <Typography component="legend" sx={{ mb: 1 }}>Favorite Track:</Typography>
                <FormControl fullWidth>
                  <Select
                    value={selectedTrackId}
                    onChange={(e) => setSelectedTrackId(e.target.value)}
                  >
                    {release.tracks.map((t, index) => (
                      <MenuItem key={t._id} value={t._id}>
                        {index + 1}. {t.title}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}
            
            {!user && (
              <>
                <TextField
                  margin="dense"
                  label="Your Name"
                  fullWidth
                  variant="outlined"
                  value={guestName}
                  onChange={(e) => setGuestName(e.target.value)}
                  sx={{
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: '#666666' },
                      '&:hover fieldset': { borderColor: '#666666' },
                      '&.Mui-focused fieldset': { borderColor: '#a187f2' }
                    }
                  }}
                  required
                />
                
                <TextField
                  margin="dense"
                  label="Your Email"
                  type="email"
                  fullWidth
                  variant="outlined"
                  value={guestEmail}
                  onChange={(e) => setGuestEmail(e.target.value)}
                  sx={{
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: '#666666' },
                      '&:hover fieldset': { borderColor: '#666666' },
                      '&.Mui-focused fieldset': { borderColor: '#a187f2' }
                    }
                  }}
                  required
                  helperText="Your email is needed to verify your download eligibility"
                />
              </>
            )}
            
            <TextField
              margin="dense"
              label="Your Comment"
              fullWidth
              variant="outlined"
              multiline
              rows={4}
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: '#666666' },
                  '&:hover fieldset': { borderColor: '#666666' },
                  '&.Mui-focused fieldset': { borderColor: '#a187f2' }
                }
              }}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => {
          setCommentDialogOpen(false);
          setCommentError('');
          setActiveTab('verify');
        }}>
          Cancel
        </Button>
        <Button 
          onClick={activeTab === 'verify' ? handleVerifyComment : handleCommentSubmit}
          variant="contained"
          disabled={submittingComment}
        >
          {submittingComment ? 'Submitting...' : activeTab === 'verify' ? 'Verify' : 'Submit Comment'}
        </Button>
      </DialogActions>
    </Dialog>
  );

  // Update the comments section JSX to only show when showComments is true
  const renderCommentsSection = () => {
    if (!showComments) {
      return (
        <Box sx={{ py: 2, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Verify your email to see comments
          </Typography>
        </Box>
      );
    }

    return loadingComments ? (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress size={24} />
      </Box>
    ) : comments.length > 0 ? (
      comments.map((comment) => (
        <Box 
          key={comment._id} 
          sx={{ 
            mb: 2, 
            p: 2, 
            borderRadius: 1,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {comment.isGuest ? (
                <Avatar sx={{ width: 32, height: 32, mr: 1 }}>
                  {comment.guestName?.charAt(0) || "G"}
                </Avatar>
              ) : (
                <Avatar 
                  src={getProfileImageUrl(comment.user?.profileImage)}
                  alt={comment.user?.username || ""}
                  sx={{ width: 32, height: 32, mr: 1 }}
                />
              )}
              
              <Typography variant="subtitle2">
                {comment.isGuest ? comment.guestName : (comment.user?.artistName || comment.user?.username || "Unknown User")}
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
                <Rating 
                  value={comment.rating} 
                  readOnly
                  size="small"
                  sx={{
                    '& .MuiRating-iconFilled': {
                      color: '#a187f2',
                    }
                  }}
                />
              </Box>
              
              {comment.timestamp && (
                <Chip 
                  label={`${Math.floor(comment.timestamp / 60)}:${String(Math.floor(comment.timestamp % 60)).padStart(2, '0')}`}
                  size="small"
                  variant="outlined"
                  sx={{ ml: 1 }}
                />
              )}
            </Box>
            
            {canEditComment(comment) && (
              <IconButton 
                size="small" 
                onClick={(event) => handleCommentMenuOpen(event, comment)}
              >
                <MoreVertIcon />
              </IconButton>
            )}
          </Box>
          
          <Typography variant="body2">{comment.text}</Typography>
        </Box>
      ))
    ) : (
      <Box sx={{ py: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No comments found for this email address.
        </Typography>
      </Box>
    );
  };

  return (
    <Container maxWidth="lg">
      <Alert severity="info" sx={{ mb: 3 }}>
        You're accessing this {release ? 'release' : 'track'} via a promo link from {getArtistDisplayName((release || track)?.artist)}.
        {!user && " You can comment and rate as a guest!"}
      </Alert>
      
      <Grid container spacing={4}>
        {/* Release/Track Info Section */}
        <Grid item xs={12} md={8}>
          {/* Cover and Basic Info */}
          <Paper sx={{ mb: 4, overflow: 'hidden' }}>
            <Grid container>
              <Grid item xs={12} sm={4}>
                <Box 
                  sx={{
                    width: '100%',
                    paddingTop: '100%',
                    position: 'relative',
                    overflow: 'hidden',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1
                  }}
                >
                  <Box
                    component="img"
                    src={getCoverArtUrl((release || track)?.coverArt)}
                    alt={(release || track)?.title}
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                </Box>
              </Grid>
              
              <Grid item xs={12} sm={8}>
                <Box sx={{ p: 3 }}>
                  <Typography variant="h4" gutterBottom>{release ? release.title : track?.title}</Typography>
                  
                  {/* Artist Info */}
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle1" color="text.secondary" sx={{ mr: 1 }}>
                        Artist(s):
                      </Typography>
                      <Typography variant="subtitle1">
                        {release ? (
                          getArtistsList(release)
                        ) : (
                          <>
                            {/* Show main artists first */}
                            {track?.featuredArtists?.filter(a => a.role === 'main').map(artist => artist.name).join(', ') ||
                             getArtistDisplayName(track?.artist)}
                            {/* Show featured artists */}
                            {track?.featuredArtists?.filter(a => a.role !== 'main').map((artist, idx) => (
                              <span key={idx}>
                                {idx === 0 ? ' feat. ' : ', '}
                                {artist.name}
                              </span>
                            ))}
                          </>
                        )}
                      </Typography>
                    </Box>

                    {/* Genre */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle1" color="text.secondary" sx={{ mr: 1 }}>
                        Genre:
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                        <Chip 
                          label={(release || track)?.primaryGenre} 
                          color="primary" 
                          size="small"
                        />
                        {(release || track)?.secondaryGenres?.map(genre => (
                          <Chip 
                            key={genre} 
                            label={genre} 
                            size="small" 
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    </Box>

                    {/* Label */}
                    {(release || track)?.label && (
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle1" color="text.secondary" sx={{ mr: 1 }}>
                          Label:
                        </Typography>
                        <Typography variant="subtitle1">
                          {(release || track)?.label}
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {/* Tags */}
                  {(release || track)?.tags && (release || track)?.tags.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                      {(release || track)?.tags.map(tag => (
                        <Chip 
                          key={tag} 
                          label={tag} 
                          size="small" 
                          variant="outlined" 
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              </Grid>
            </Grid>
            
            {/* Bio/Description Section */}
            {(release || track)?.description && (
              <Box sx={{ p: 3, pt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {(release || track)?.description}
                </Typography>
              </Box>
            )}
          </Paper>

          {/* Track List with integrated players */}
          <Paper sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ p: 3, pb: 2 }} gutterBottom>
              {release ? 'Tracks' : 'Track'}
            </Typography>
            <Box sx={{ px: 2, pb: 2 }}>
              <Box sx={{ p: 2 }}>
                {(release ? release.tracks : [track]).map((t, index) => (
                  <Box
                    key={t._id}
                    sx={{
                      bgcolor: t._id === selectedTrackId ? 'action.selected' : 'background.paper',
                      mb: 1,
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      '&:last-child': {
                        mb: 0
                      },
                      overflow: 'hidden'
                  }}
                >
                  <Box sx={{ 
                    p: 2,
                    display: 'flex', 
                    alignItems: 'center',
                  }}>
                    <IconButton 
                      onClick={() => {
                        if (t._id === selectedTrackId) {
                          togglePlayPause();
                        } else {
                          setSelectedTrackId(t._id);
                          setTrack(t);
                          if (audioRef.current) {
                            audioRef.current.pause();
                            setPlaying(false);
                          }
                        }
                      }}
                      color={t._id === selectedTrackId ? 'primary' : 'default'}
                      size="small"
                    >
                      {t._id === selectedTrackId && playing ? <PauseIcon /> : <PlayIcon />}
                    </IconButton>
                    <Box sx={{ flexGrow: 1, minWidth: 0, ml: 2 }}>
                      <Typography variant="body1" noWrap>
                        {`${(index + 1).toString().padStart(2, '0')}. ${t.title}`}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {/* Show main artists first */}
                        {t.featuredArtists?.filter(a => a.role === 'main').map(artist => artist.name).join(', ') ||
                         getArtistDisplayName(t.artist)}
                        {/* Show featured artists */}
                        {t.featuredArtists?.filter(a => a.role !== 'main').map((artist, idx) => (
                          <span key={idx}>
                            {idx === 0 ? ' feat. ' : ', '}
                            {artist.name}
                          </span>
                        ))}
                      </Typography>
                    </Box>
                    
                    {/* Download button for individual tracks */}
                    {canDownload ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
                        {availableFormats.length > 1 && (
                          <FormControl size="small" sx={{ minWidth: 60, mr: 1 }}>
                            <Select
                              value={selectedFormat}
                              onChange={(e) => setSelectedFormat(e.target.value)}
                              displayEmpty
                              sx={{ 
                                fontSize: '0.75rem',
                                '& .MuiSelect-select': { py: 0.5 }
                              }}
                            >
                              {availableFormats.map((format) => (
                                <MenuItem key={format} value={format} sx={{ fontSize: '0.75rem' }}>
                                  {format.toUpperCase()}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}
                        <IconButton 
                          onClick={() => downloadTrack(t._id, selectedFormat)}
                          color="primary"
                          size="small"
                          title={`Download ${t.title} (${selectedFormat.toUpperCase()})`}
                          disabled={downloadingTrack === t._id}
                        >
                          {downloadingTrack === t._id ? (
                            <CircularProgress size={20} />
                          ) : (
                            <DownloadIcon />
                          )}
                        </IconButton>
                      </Box>
                    ) : (
                      <IconButton 
                        onClick={() => {
                          setCommentDialogOpen(true);
                        }}
                        color="default"
                        size="small"
                        sx={{ ml: 1 }}
                        title="Comment to download"
                      >
                        <CommentIcon />
                      </IconButton>
                    )}
                  </Box>
                  
                  {t._id === selectedTrackId && playing && (
                    <Box sx={{ 
                      px: 2,
                      pb: 2,
                    }}>
                      <Slider
                        size="small"
                        value={(currentTime / duration) * 100 || 0}
                        onChange={handleSliderChange}
                        sx={{
                          '& .MuiSlider-thumb': {
                            width: 12,
                            height: 12,
                          }
                        }}
                      />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">
                          {formatTime(currentTime)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatTime(duration)}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Box>
              ))}
              </Box>
            </Box>
          </Paper>

          {/* Download/Comment Section */}
          <Paper sx={{ p: 3, mb: 4 }}>
            {/* Format Selector - show when download is available */}
            {canDownload && availableFormats.length > 1 && (
              <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
                <FormControl sx={{ minWidth: 200 }}>
                  <InputLabel>Download Format</InputLabel>
                  <Select
                    value={selectedFormat}
                    label="Download Format"
                    onChange={(e) => setSelectedFormat(e.target.value)}
                    size="small"
                  >
                    {availableFormats.map((format) => (
                      <MenuItem key={format} value={format}>
                        {format.toUpperCase()} {format === 'mp3' ? '(Compressed)' : '(Lossless)'}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}
            
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
              {canDownload ? (
                <>
                  {/* Full Release Download - only show if this is a multi-track release */}
                  {release && release.tracks && release.tracks.length > 1 && (
                    <Button 
                      variant="contained" 
                      startIcon={downloadingRelease ? <CircularProgress size={20} /> : <DownloadIcon />}
                      onClick={downloadRelease}
                      color="primary"
                      size="large"
                      disabled={downloadingRelease}
                    >
                      {downloadingRelease ? 'Preparing Download...' : `Download Full Release (${selectedFormat.toUpperCase()})`}
                    </Button>
                  )}
                  
                  {/* Message for single track downloads */}
                  {(!release || !release.tracks || release.tracks.length === 1) && (
                    <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                      Use the download icon next to the track to download it in {selectedFormat.toUpperCase()} format.
                    </Typography>
                  )}
                </>
              ) : (
                <Button 
                  variant="contained" 
                  startIcon={<CommentIcon />}
                  onClick={() => {
                    setCommentDialogOpen(true);
                    setActiveTab(user ? 'comment' : 'verify');
                  }}
                  size="large"
                >
                  Comment To Download
                </Button>
              )}
            </Box>
          </Paper>

          {/* Comments Section */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Comments & Feedback
            </Typography>
            {renderCommentsSection()}
          </Paper>
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} md={4}>
          {/* Artist Info */}
          <Paper sx={{ p: 3, mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              About {getArtistDisplayName((release || track)?.artist)}
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Avatar
                src={getProfileImageUrl((release || track)?.artist.profileImage)}
                alt={getArtistDisplayName((release || track)?.artist)}
                sx={{ width: 64, height: 64, mr: 2 }}
              />
              <Box>
                <Typography variant="subtitle1">
                  {getArtistDisplayName((release || track)?.artist)}
                </Typography>
              </Box>
            </Box>
            
            {(release || track)?.artist.bio && (
              <Typography variant="body2" paragraph>
                {(release || track)?.artist.bio}
              </Typography>
            )}
            
            {(release || track)?.artist.socialLinks && Object.values((release || track)?.artist.socialLinks).some(Boolean) && (
              <Box sx={{ mb: 2 }}>
                <Stack direction="row" spacing={1}>
                  {(release || track)?.artist.socialLinks.instagram && (
                    <IconButton 
                      color="primary" 
                      component="a" 
                      href={getSocialDisplayLink((release || track)?.artist.socialLinks.instagram, 'instagram')}
                      target="_blank"
                      size="small"
                    >
                      <InstagramIcon />
                    </IconButton>
                  )}
                  {(release || track)?.artist.socialLinks.facebook && (
                    <IconButton 
                      color="primary" 
                      component="a" 
                      href={getSocialDisplayLink((release || track)?.artist.socialLinks.facebook, 'facebook')}
                      target="_blank"
                      size="small"
                    >
                      <FacebookIcon />
                    </IconButton>
                  )}
                  {(release || track)?.artist.socialLinks.twitter && (
                    <IconButton 
                      color="primary" 
                      component="a" 
                      href={getSocialDisplayLink((release || track)?.artist.socialLinks.twitter, 'twitter')}
                      target="_blank"
                      size="small"
                    >
                      <TwitterIcon />
                    </IconButton>
                  )}
                  {(release || track)?.artist.socialLinks.website && (
                    <IconButton 
                      color="primary" 
                      component="a" 
                      href={getSocialDisplayLink((release || track)?.artist.socialLinks.website, 'website')}
                      target="_blank"
                      size="small"
                    >
                      <LanguageIcon />
                    </IconButton>
                  )}
                </Stack>
              </Box>
            )}
          </Paper>

          {/* How It Works */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              How It Works
            </Typography>
            <Typography variant="body2" paragraph>
              1. Listen to the track for free
            </Typography>
            <Typography variant="body2" paragraph>
              2. Leave a rating and comment with your feedback
            </Typography>
            <Typography variant="body2" paragraph>
              3. Download the track after commenting
            </Typography>
            <Alert severity="info" sx={{ mt: 2 }}>
              Your feedback helps artists improve their music!
            </Alert>
          </Paper>

          {/* Curator Tools - Show only for curators */}
          {user && user.accountType === 'curator' && (
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Curator Tools
              </Typography>
              
              {libraryEntry && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  ✓ This {release ? 'release' : 'track'} is in your library
                </Alert>
              )}
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<LibraryAddIcon />}
                  onClick={() => setShowLibraryDialog(true)}
                  fullWidth
                >
                  {libraryEntry ? 'Update Library Entry' : 'Add to Library'}
                </Button>
                
                <Button
                  variant="outlined"
                  startIcon={<PlaylistPlayIcon />}
                  onClick={navigateToPlayLog}
                  fullWidth
                >
                  Log Play
                </Button>
                
                <Button
                  variant="outlined"
                  startIcon={<LibraryBooksIcon />}
                  onClick={navigateToLibrary}
                  fullWidth
                >
                  View My Library
                </Button>
              </Box>
            </Paper>
          )}
        </Grid>
      </Grid>

      {/* Keep existing dialogs */}
      <Dialog open={downloadDialogOpen} onClose={() => setDownloadDialogOpen(false)}>
        <DialogTitle>Verify Download</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Enter your email to verify if you've already commented on this track.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Email Address"
            type="email"
            fullWidth
            variant="outlined"
            value={downloadEmail}
            onChange={(e) => setDownloadEmail(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: '#666666' },
                '&:hover fieldset': { borderColor: '#666666' },
                '&.Mui-focused fieldset': { borderColor: '#a187f2' }
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDownloadDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDownloadVerify} variant="contained">
            Verify
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Comment dialog */}
      {renderCommentDialog()}
      
      {/* Success message snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage('')}
        message={successMessage}
      />

      {/* Download message snackbar */}
      <Snackbar
        open={!!downloadMessage}
        autoHideDuration={6000}
        onClose={() => setDownloadMessage('')}
        message={downloadMessage}
      />

      {/* Comment menu */}
      <Menu
        anchorEl={commentMenuAnchor}
        open={Boolean(commentMenuAnchor)}
        onClose={handleCommentMenuClose}
      >
        <MenuItem onClick={handleEditMenuClick}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit Comment</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDeleteMenuClick}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Delete Comment</ListItemText>
        </MenuItem>
      </Menu>

      {/* Edit comment dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Comment</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Rating
            </Typography>
            <Rating
              name="edit-rating"
              value={editCommentRating}
              onChange={(event, newValue) => {
                setEditCommentRating(newValue);
              }}
              sx={{
                '& .MuiRating-iconFilled': {
                  color: '#a187f2',
                },
                '& .MuiRating-iconHover': {
                  color: '#a187f2',
                }
              }}
            />
          </Box>
          
          <TextField
            autoFocus
            margin="dense"
            label="Comment"
            type="text"
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            value={editCommentText}
            onChange={(e) => setEditCommentText(e.target.value)}
            error={!!commentError}
            helperText={commentError}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: '#666666' },
                '&:hover fieldset': { borderColor: '#666666' },
                '&.Mui-focused fieldset': { borderColor: '#a187f2' }
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setEditDialogOpen(false);
            setCommentError('');
            setEditingComment(null);
            setEditCommentText('');
            setEditCommentRating(0);
          }}>
            Cancel
          </Button>
          <Button 
            onClick={handleSaveEditedComment}
            variant="contained"
            disabled={submittingComment}
          >
            {submittingComment ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Library Entry Dialog */}
      <Dialog open={showLibraryDialog} onClose={() => setShowLibraryDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {libraryEntry ? 'Update Library Entry' : 'Add to Library'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Rating (Optional)
            </Typography>
            <Rating
              name="library-rating"
              value={libraryRating}
              onChange={(event, newValue) => {
                setLibraryRating(newValue);
              }}
              sx={{
                '& .MuiRating-iconFilled': {
                  color: '#a187f2',
                },
                '& .MuiRating-iconHover': {
                  color: '#a187f2',
                }
              }}
            />
          </Box>
          
          <TextField
            margin="dense"
            label="Notes (Optional)"
            type="text"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={libraryNotes}
            onChange={(e) => setLibraryNotes(e.target.value)}
            placeholder="Add your thoughts, reminders, or context about this track/release..."
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: '#666666' },
                '&:hover fieldset': { borderColor: '#666666' },
                '&.Mui-focused fieldset': { borderColor: '#a187f2' }
              }
            }}
          />

          <TextField
            margin="dense"
            label="Tags (Optional)"
            type="text"
            fullWidth
            variant="outlined"
            value={libraryTags}
            onChange={(e) => setLibraryTags(e.target.value)}
            placeholder="electronic, house, festival, etc. (comma separated)"
            helperText="Separate tags with commas"
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: '#666666' },
                '&:hover fieldset': { borderColor: '#666666' },
                '&.Mui-focused fieldset': { borderColor: '#a187f2' }
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setShowLibraryDialog(false);
            setLibraryNotes('');
            setLibraryRating(0);
            setLibraryTags('');
          }}>
            Cancel
          </Button>
          <Button 
            onClick={updateLibraryEntry}
            variant="contained"
          >
            {libraryEntry ? 'Update' : 'Add to Library'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default PromoPage;